import { apiClient } from '../api-client';
import type { ApiResponse } from '@/types/api';

export interface AuditLogEntry {
  id: number;
  userId: number;
  userName: string;
  action: string;
  entityType: string;
  entityId: string;
  details: string;
  timestamp: string;
}

export interface PagedAuditResponse {
  items: AuditLogEntry[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface AuditQueryParams {
  entityType?: string;
  action?: string;
  userId?: number;
  from?: string;
  to?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface BarcodeCurrentState {
  manifestBarcodeId: number;
  manifestId: number;
  manifestName: string;
  status: string;
  isDeleted: boolean;
  locationCode?: string;
  sortId1?: string;
  sortId2?: string;
  receivedAt: string;
}

export interface BarcodeTimelineEvent {
  eventType: 'Scan' | 'Movement' | 'Pick' | 'Admin';
  action: string;
  userName: string;
  timestamp: string;
  details: string;
  manifestName?: string;
}

export interface BarcodeTimelineResponse {
  barcodeValue: string;
  totalManifests: number;
  currentState?: BarcodeCurrentState;
  timeline: BarcodeTimelineEvent[];
}

export interface UnifiedAuditEvent {
  sourceType: 'Admin' | 'Scan' | 'Movement';
  userName: string;
  action: string;
  details: string;
  timestamp: string;
  barcodeValue?: string;
  manifestName?: string;
  entityType?: string;
  entityId?: string;
}

export interface UnifiedAuditResponse {
  items: UnifiedAuditEvent[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface UnifiedAuditQueryParams {
  source?: string;
  from?: string;
  to?: string;
  pageNumber?: number;
  pageSize?: number;
}

export const auditApi = {
  getAll: async (params?: AuditQueryParams): Promise<PagedAuditResponse> => {
    const response = await apiClient.get<ApiResponse<PagedAuditResponse>>('/audit', { params });
    return response.data.data!;
  },

  getUnified: async (params?: UnifiedAuditQueryParams): Promise<UnifiedAuditResponse> => {
    const response = await apiClient.get<ApiResponse<UnifiedAuditResponse>>('/audit/unified', { params });
    return response.data.data!;
  },

  getBarcodeTimeline: async (barcodeValue: string): Promise<BarcodeTimelineResponse> => {
    const response = await apiClient.get<ApiResponse<BarcodeTimelineResponse>>(
      `/audit/barcode/${encodeURIComponent(barcodeValue)}`
    );
    return response.data.data!;
  },
};
